import { CloseOutlined, PlusOutlined } from '@ant-design/icons'
import { Link } from '@remix-run/react'
import {
  Button,
  Card,
  Divider,
  Flex,
  Form,
  Input,
  theme,
  Typography,
} from 'antd'
import React, { useEffect, useMemo } from 'react'
import { styled } from 'styled-components'
import { InputDebounced, JsonInput } from '~/features/shared/components'
import {
  ConfigurationType,
  FormInputType,
  StepType,
} from '../../../common/types'
import { FormInput } from '../FormInput'

const { useToken } = theme

const BadgeWrapper = styled.div`
  position: absolute;
  left: -1px;
  top: 0;
  transform: translateY(-100%);
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
  background: red;
  color: white;
`

const FormWrapper = styled(Form<StepType>)`
  .ant-form-item {
    margin-bottom: 8px;
  }
`

type Props = {
  index: number
  configuration: ConfigurationType
  step: StepType
  onChange: (values: ConfigurationType) => void
  onChangeInstant: (values: ConfigurationType) => void
}

export const Step: React.FC<Props> = ({
  index,
  configuration,
  step,
  onChange,
  onChangeInstant,
}) => {
  const { token } = useToken()

  const initialValues = useMemo(
    () => ({
      ...step,
      initialValues: step.initialValues ?? {},
    }),
    [step],
  )

  const countSteps = configuration.steps.length

  const formInputs: FormInputType[] = useMemo(() => {
    return step.inputs ?? []
  }, [step.inputs])

  const [form] = Form.useForm<StepType>()

  useEffect(() => {
    form.setFieldsValue(initialValues)
  }, [initialValues])

  const handleChangeStep = (
    _values: Partial<StepType>,
    valuesFull: Partial<StepType>,
  ) => {
    handleChange(valuesFull)
  }

  const getKey = (name: string) => {
    return `${step.key}.${name}`
  }

  const handleChange = (values: Partial<StepType>) => {
    const stepsUpdated = [...configuration.steps]

    stepsUpdated[index] = { ...step, ...values }

    onChange({
      ...configuration,
      steps: stepsUpdated,
    })
  }

  const handleChangeInstant = (values: Partial<StepType>) => {
    const stepsUpdated = [...configuration.steps]

    stepsUpdated[index] = { ...step, ...values }

    onChangeInstant({
      ...configuration,
      steps: stepsUpdated,
    })
  }

  const handleChangeOrder = (indexBefore: number, indexAfter: number) => {
    const inputsUpdated = [...configuration.steps[index].inputs]

    const isOutOfRange = indexAfter < 0 || indexAfter > inputsUpdated.length - 1
    if (isOutOfRange) {
      return
    }

    const inputBefore = inputsUpdated[indexBefore]
    const inputAfter = inputsUpdated[indexAfter]

    inputsUpdated[indexBefore] = inputAfter
    inputsUpdated[indexAfter] = inputBefore

    const stepsUpdated = [...configuration.steps]

    stepsUpdated[index].inputs = inputsUpdated

    onChangeInstant({
      ...configuration,
      steps: stepsUpdated,
    })
  }

  const handleAddFormInput = () => {
    const key = `key${formInputs.length}`

    const inputs: FormInputType[] = [
      ...formInputs,
      { key, inputType: 'Input', propsFormItem: {}, propsFormInput: {} },
    ]
    const stepsUpdated = [...configuration.steps]

    stepsUpdated[index] = { ...step, inputs }

    onChangeInstant({
      ...configuration,
      steps: stepsUpdated,
    })
  }

  const handleClickDelete = () => {
    const stepsUpdated = [...configuration.steps]

    stepsUpdated.splice(index, 1)

    onChangeInstant({
      ...configuration,
      steps: stepsUpdated,
    })
  }

  return (
    <>
      <Card
        style={{
          borderTop: `10px solid ${token.colorPrimary}`, // Colored top border
        }}
      >
        <BadgeWrapper
          className="p-2"
          style={{ background: token.colorPrimary }}
        >
          Step {index + 1} of {countSteps}
        </BadgeWrapper>

        <Flex vertical>
          <FormWrapper
            form={form}
            initialValues={initialValues}
            layout="vertical"
            onValuesChange={handleChangeStep}
          >
            <Flex justify="space-between" align="center">
              <Form.Item htmlFor={getKey('key')} name="key">
                <Input
                  id={getKey('key')}
                  prefix={
                    <Typography.Text type="secondary">
                      <i className="las la-key"></i>
                    </Typography.Text>
                  }
                  placeholder="step-key"
                  variant="borderless"
                />
              </Form.Item>

              <Button
                icon={<CloseOutlined />}
                danger
                type="text"
                size="small"
                onClick={handleClickDelete}
              />
            </Flex>

            <Form.Item htmlFor={getKey('title')} name="title">
              <InputDebounced
                id={getKey('title')}
                styleKey="title-2"
                delaySeconds={0}
                variant="borderless"
                placeholder="Title"
              />
            </Form.Item>

            <Form.Item htmlFor={getKey('description')} name="description">
              <InputDebounced
                id={getKey('description')}
                delaySeconds={0}
                variant="borderless"
                placeholder="Description"
              />
            </Form.Item>

            <Form.Item
              htmlFor={getKey('ifConditions')}
              name={['ifConditions']}
              label={
                <Typography.Text type="secondary">
                  IF Conditions using{' '}
                  <Link
                    to="https://www.npmjs.com/package/jexl"
                    target="_blank"
                    rel="noreferrer"
                  >
                    jexl
                  </Link>
                </Typography.Text>
              }
            >
              <Input.TextArea
                id={getKey('ifConditions')}
                placeholder="step.input == true"
                variant="borderless"
                autoSize={{ minRows: 1, maxRows: 4 }}
              />
            </Form.Item>

            <Form.Item
              name={['initialValues']}
              htmlFor={getKey('initialValues')}
              label="Initial Values"
            >
              <JsonInput id={getKey('initialValues')} />
            </Form.Item>
          </FormWrapper>

          <Divider />

          <Flex vertical gap={16}>
            {formInputs.map((formInput, formInputIndex) => (
              <FormInput
                key={`${index}-${formInputIndex}-${step.key}-${formInput.key}`}
                index={formInputIndex}
                formInput={formInput}
                step={step}
                onChange={handleChange}
                onChangeInstant={handleChangeInstant}
                onMoveUp={() =>
                  handleChangeOrder(formInputIndex, formInputIndex - 1)
                }
                onMoveDown={() =>
                  handleChangeOrder(formInputIndex, formInputIndex + 1)
                }
              />
            ))}

            <Flex>
              <Button icon={<PlusOutlined />} onClick={handleAddFormInput}>
                Add question
              </Button>
            </Flex>
          </Flex>
        </Flex>
      </Card>
    </>
  )
}
